import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight } from 'lucide-react';

const PricingPage: React.FC = () => {
  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 0,
      description: 'Perfect for trying out WorkOrder HQ™',
      features: [
        { text: 'Up to 3 users', included: true },
        { text: 'Unlimited work orders', included: true },
        { text: 'Client & vendor management', included: true },
        { text: 'Work order tracking', included: true },
        { text: 'File attachments', included: true },
        { text: 'Timeline & notes', included: true },
        { text: 'Invoicing & billing', included: false },
        { text: 'Reports & analytics', included: false },
        { text: 'Vendor compliance & COI tracking', included: false },
        { text: 'API access', included: false },
      ],
      cta: 'Get Started Free',
      popular: false,
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 79,
      description: 'For growing businesses',
      features: [
        { text: 'Up to 10 users', included: true },
        { text: 'Unlimited work orders', included: true },
        { text: 'Client & vendor management', included: true },
        { text: 'Work order tracking', included: true },
        { text: 'File attachments', included: true },
        { text: 'Timeline & notes', included: true },
        { text: 'Invoicing & billing', included: true },
        { text: 'Reports & analytics', included: true },
        { text: 'Bulk operations', included: true },
        { text: 'Priority support', included: true },
        { text: 'Vendor compliance & COI tracking', included: false },
        { text: 'API access', included: false },
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 299,
      description: 'For large organizations',
      features: [
        { text: 'Unlimited users', included: true },
        { text: 'Unlimited work orders', included: true },
        { text: 'Client & vendor management', included: true },
        { text: 'Work order tracking', included: true },
        { text: 'File attachments', included: true },
        { text: 'Timeline & notes', included: true },
        { text: 'Invoicing & billing', included: true },
        { text: 'Reports & analytics', included: true },
        { text: 'Bulk operations', included: true },
        { text: 'API access', included: true },
        { text: 'Priority support', included: true },
        { text: 'Advanced security', included: true },
        { text: 'Vendor compliance & COI tracking', included: true },
        { text: 'Custom integrations', included: true },
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Choose the plan that fits your business. All plans include a 14-day free trial.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden ${
                plan.popular ? 'ring-2 ring-blue-600 scale-105 transform' : 'border border-gray-200 dark:border-gray-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}

              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900 dark:text-white">
                    ${plan.price}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    /month
                  </span>
                </div>

                <Link
                  to={`/signup?plan=${plan.id}`}
                  className={`block w-full py-3 px-6 text-center font-semibold rounded-lg transition-all ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-105'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {plan.cta}
                </Link>

                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                      )}
                      <span
                        className={`text-sm ${
                          feature.included
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Can I try before I buy?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Yes! All plans include a 14-day free trial with full access to Professional features. No credit card required.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Can I change plans later?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately for upgrades, or at the end of your billing cycle for downgrades.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We accept all major credit cards (Visa, MasterCard, American Express) through our secure payment processor, Stripe.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Is there a contract or can I cancel anytime?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                No contracts required! All subscriptions are month-to-month and you can cancel anytime. Your data will be available for 30 days after cancellation.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                We don't offer refunds for partial months, but you can cancel anytime and won't be charged again.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Is my data secure?
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Yes! We use enterprise-grade security with encryption, role-based access controls, and regular backups to keep your data safe.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our team is here to help you choose the right plan for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:sales@workorderhq.com"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold text-blue-600 bg-white rounded-lg hover:bg-gray-100 transition-all"
            >
              Contact Sales
            </a>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-blue-600 transition-all"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
